import { SFStack } from "../grid";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import SFTypography from "../typography";
import SFButton from "../button";
import getStyled from "../../hooks/getStyled";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

const { styled } = getStyled();

const StyledSFStack = styled(SFStack)(({ theme }) => ({
  padding: "20px",
  border: `1px solid ${theme.palette.grey200.main}`,
  borderRadius: "4px",
  boxShadow: "-2px 4px 12px 0px rgba(24, 24, 24, 0.08)",
  backgroundColor: theme.palette.white.main,
  "&:hover": {
    backgroundColor: theme.palette.grey100.main,
    "& .stack-container": {
      backgroundColor: theme.palette.white.main,
    },
    "& .card-button": {
      backgroundColor: theme.palette.grey200.main,
    },
  },
}));

const StyledCardTitle = styled("div")(({ theme }) => ({
  textOverflow: "ellipsis",
  overflow: "hidden",
  display: "-webkit-box",
  webkitLineClamp: "2",
  webkitBoxOrient: "vertical",
  height: 45,
}));

const SFStackContainer = styled(SFStack)(({ theme }) => ({
  width: "100%",
  height: "100%",
  maxWidth: "240px",
  maxHeight: "147px",
  display: "flex",
  alignItems: "flex-start",
  paddingLeft: "6px",
  paddingTop: "5px",
  backgroundColor: theme.palette.grey100.main,
  border: `1px solid ${theme.palette.grey200.main}`,
  borderRadius: "4px",
}));

const StyledChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== "padding",
})(({ theme, recommended }) => ({
  visibility: recommended ? "visible" : "hidden",
  pointerEvents: recommended ? "auto" : "none",
  color: theme.palette.white.main,
  backgroundColor: theme.palette.maroon200.main,
}));

const ChipTypography = styled(Typography)(({ theme }) => ({
  color: "white",
  fontSize: "10px",
  fontWeight: 300,
}));

const LogoContainer = styled("div")(({ theme }) => ({
  alignSelf: "center",
  height: "100%",
  display: "flex",
  alignItems: "center",
  overflow: "hidden",
  "& .MuiSvgIcon-root": {
    width: "unset",
    height: "120px",
  },
}));

const SFInstructionalCard = ({
  logo,
  title,
  description,
  buttonLabel,
  onClick,
  statistics,
  disabled,
  recommended = false,
  buttonIcon = null,
}) => {
  const { t } = useTranslation();

  return (
    <StyledSFStack
      direction="row"
      justifyContent="flex-start"
      height="100%"
      spacing={2}
    >
      <SFStackContainer
        className="stack-container"
        direction="column"
        justifyContent="flex-start"
      >
        <StyledChip
          size="small"
          recommended={recommended}
          label={
            <ChipTypography>
              {t("components.instructionalCard.recommendedLabel")}
            </ChipTypography>
          }
        />
        <LogoContainer>{logo}</LogoContainer>
      </SFStackContainer>
      <SFStack direction="column" justifyContent="space-between" width="100%">
        <SFStack direction="column" justifyContent="flex-start">
          <StyledCardTitle>
            <SFTypography variant="cardTitle">{title}</SFTypography>
          </StyledCardTitle>
          <SFTypography variant="body1" color="grey400">
            {description}
          </SFTypography>
        </SFStack>
        <SFStack
          direction="row"
          justifyContent={statistics?.length > 0 ? "space-between" : "flex-end"}
          alignItems="center"
        >
          {statistics && (
            <SFStack direction="row" alignItems="flex-end">
              <SFTypography variant="cardNumber" sx={{ marginRight: "4px" }}>
                {statistics[0]?.value}
              </SFTypography>
              <SFTypography variant="cardNumberText">
                {statistics[0]?.description}
              </SFTypography>
            </SFStack>
          )}
          <SFButton
            className="card-button"
            variant="utility"
            onClick={onClick}
            fullWidth={false}
            disabled={disabled}
            endIcon={buttonIcon}
          >
            {buttonLabel}
          </SFButton>
        </SFStack>
      </SFStack>
    </StyledSFStack>
  );
};

SFInstructionalCard.propTypes = {
  logo: PropTypes.element.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.oneOfType([PropTypes.element, PropTypes.string])
    .isRequired,
  buttonLabel: PropTypes.string.isRequired,
  buttonIcon: PropTypes.element,
  onClick: PropTypes.func.isRequired,
  statistics: PropTypes.array,
  recommended: PropTypes.bool,
};

export default SFInstructionalCard;
